/**
 * LH Drawer — Shared drawer/sidebar controller
 * assets/lh-drawer.js
 *
 * Usage:
 *   LHDrawer.register('my-drawer', { el: document.getElementById('my-drawer') });
 *   LHDrawer.open('my-drawer');
 *   LHDrawer.close('my-drawer');
 *   LHDrawer.toggle('my-drawer');
 *
 * Options:
 *   el       (required) — the .lh-drawer root element
 *   onOpen   (optional) — callback fired after drawer opens
 *   onClose  (optional) — callback fired after drawer closes
 */
window.LHDrawer = (function () {
  'use strict';

  var drawers = {};
  var activeId = null;

  /**
   * Register a drawer for open/close management.
   * Auto-wires overlay click, close button, and data-lh-drawer-close elements.
   */
  function register(id, options) {
    if (!options || !options.el) return;

    var drawer = {
      el: options.el,
      onOpen: options.onOpen || null,
      onClose: options.onClose || null,
      trigger: null
    };

    // Wire overlay click → close
    var overlay = drawer.el.querySelector('[data-lh-drawer-overlay]');
    if (overlay) {
      overlay.addEventListener('click', function () {
        close(id);
      });
    }

    // Wire all close buttons → close
    var closeBtns = drawer.el.querySelectorAll('[data-lh-drawer-close]');
    for (var i = 0; i < closeBtns.length; i++) {
      closeBtns[i].addEventListener('click', function () {
        close(id);
      });
    }

    drawers[id] = drawer;
  }

  /**
   * Open a drawer by id.
   * Closes any other open drawer first (only one at a time).
   *
   * Optional overrides object:
   *   { title: 'New Title', content: '<p>HTML</p>', eyebrow: 'Text', subtitle: 'Text' }
   */
  function open(id, overrides) {
    var drawer = drawers[id];
    if (!drawer) return;

    // Close any currently open drawer
    if (activeId && activeId !== id) {
      close(activeId);
    }

    // Apply dynamic content overrides if provided
    if (overrides) {
      if (overrides.title) {
        var titleEl = drawer.el.querySelector('[data-lh-drawer-title]');
        if (titleEl) titleEl.textContent = overrides.title;
      }
      if (overrides.content) {
        var bodyEl = drawer.el.querySelector('.lh-drawer__body');
        if (bodyEl) bodyEl.innerHTML = overrides.content;
      }
      if (overrides.eyebrow) {
        var eyebrowEl = drawer.el.querySelector('.lh-drawer__eyebrow');
        if (eyebrowEl) eyebrowEl.textContent = overrides.eyebrow;
      }
      if (overrides.subtitle) {
        var subtitleEl = drawer.el.querySelector('.lh-drawer__subtitle');
        if (subtitleEl) subtitleEl.textContent = overrides.subtitle;
      }
    }

    drawer.trigger = document.activeElement || null;
    drawer.el.classList.add('is-open');
    drawer.el.setAttribute('aria-hidden', 'false');
    activeId = id;

    // Scroll lock — touch-event approach, no body style changes.
    //
    // Why not body { overflow:hidden }? iOS Safari snaps body to
    // scrollY 0 when overflow:hidden is set, jumping the page up.
    //
    // Why not body { position:fixed; top:-scrollY }? iOS Safari has
    // a long-standing bug where descendants with position:fixed can
    // get positioned relative to the fixed body instead of the
    // viewport — placing the drawer off-screen at the user's prior
    // scroll offset.
    //
    // Instead, just preventDefault on touchmove for any target that
    // isn't inside a scrollable drawer region. Body stays in normal
    // flow, fixed children behave correctly, and the drawer covers
    // the viewport via its own position:fixed inset:0.
    addBodyTouchLock();
    document.body.classList.add('lh-drawer-open');

    if (drawer.onOpen) {
      drawer.onOpen(overrides);
    }
  }

  function bodyTouchHandler(e) {
    // Allow scroll inside the drawer's scrollable body, block elsewhere.
    var t = e.target;
    while (t && t !== document.body) {
      if (t.classList && t.classList.contains('lh-drawer__body')) return;
      t = t.parentNode;
    }
    if (e.cancelable) e.preventDefault();
  }
  /* iOS Safari quirk: a tap on a button mid-page can trigger a small
     scroll adjustment (URL bar collapse) right before the click event
     fires, so window.scrollY at click-time is unreliable. Capture
     scrollY on every touchstart and stash it; open() reads from this
     buffer first if present. */
  var lastTouchScrollY = null;
  function captureTouchScroll() {
    lastTouchScrollY =
      (document.scrollingElement && document.scrollingElement.scrollTop) ||
      window.pageYOffset ||
      window.scrollY ||
      0;
  }
  document.addEventListener('touchstart', captureTouchScroll, { passive: true });
  function addBodyTouchLock() {
    document.addEventListener('touchmove', bodyTouchHandler, { passive: false });
  }
  function removeBodyTouchLock() {
    document.removeEventListener('touchmove', bodyTouchHandler, { passive: false });
  }

  /**
   * Close a drawer by id.
   */
  function close(id) {
    var drawer = drawers[id];
    if (!drawer) return;

    drawer.el.classList.remove('is-open');

    // Return focus to the element that opened the drawer before hiding it from
    // the a11y tree — aria-hidden on an ancestor of the focused element is a
    // violation and triggers a browser warning.
    var returnTarget = drawer.trigger;
    drawer.trigger = null;
    if (returnTarget && typeof returnTarget.focus === 'function') {
      returnTarget.focus({ preventScroll: true });
    } else {
      document.body.focus({ preventScroll: true });
    }

    drawer.el.setAttribute('aria-hidden', 'true');

    if (activeId === id) {
      activeId = null;
    }

    // Remove the touch-event lock applied on open. No body style
    // changes were made, so there's nothing else to unwind here —
    // the page stays where the user left it.
    removeBodyTouchLock();
    document.body.classList.remove('lh-drawer-open');
    // Defensive cleanup: clear any legacy scroll-lock styles set by
    // older code paths (no-op if those styles aren't present).
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.classList.remove('scroll-lock', 'no-scroll', 'overflow-hidden');
    if (document.body.dataset.lhDrawerScrollY) {
      delete document.body.dataset.lhDrawerScrollY;
    }

    if (drawer.onClose) {
      drawer.onClose();
    }
  }

  /**
   * Close all open drawers.
   */
  function closeAll() {
    for (var id in drawers) {
      if (drawers[id].el.classList.contains('is-open')) {
        close(id);
      }
    }
  }

  /**
   * Toggle a drawer open/closed.
   */
  function toggle(id) {
    if (isOpen(id)) {
      close(id);
    } else {
      open(id);
    }
  }

  /**
   * Check if a drawer is currently open.
   */
  function isOpen(id) {
    var drawer = drawers[id];
    return drawer ? drawer.el.classList.contains('is-open') : false;
  }

  // Global Escape key listener (single listener for all drawers)
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && activeId) {
      close(activeId);
    }
  });

  return {
    register: register,
    open: open,
    close: close,
    closeAll: closeAll,
    toggle: toggle,
    isOpen: isOpen
  };
})();
